import { useEffect, useMemo } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { AliasForm } from 'app/components/mapping/AliasForm'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { UtmAlias } from 'app/types/entities'
import { showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import type { SearchResponse } from 'app/services/search'
import { logError } from 'lib/logging'
import { useUtmAliasStore } from 'app/stores/StoreContext'

export type MappingMediaLoaderData = {
  media: SearchResponse<UtmAlias>
  canEditMapping: boolean
}

export type UseMappingMediaReturn = {
  media: UtmAlias[]
  loading: boolean
  canEditMapping: boolean
  onAdd: () => void
  onEdit: (utmAlias: UtmAlias) => void
  onDelete: (id: string, workspaceId: string) => Promise<void>
  columns: ColumnDef<UtmAlias, any>[]
}

export function useMappingMedia(
  getColumns: (ctx: {
    canEditMapping: boolean
    onEdit: (utmAlias: UtmAlias) => void
    onDelete: (id: string, workspaceId: string) => Promise<void>
  }) => ColumnDef<UtmAlias, any>[],
): UseMappingMediaReturn {
  const [confirm] = useConfirmation()
  const checkPermission = useCheckPermission()
  const canEditMapping = checkPermission([`org:settings:edit-mapping`, `settings:mapping:edit`])
  const store = useUtmAliasStore()

  useEffect(() => {
    store.fetchList({ search: `type:medium` })
  }, [])

  const media = store.hits as unknown as UtmAlias[]
  const loading = store.listLoading

  const openModal = ({ title, utmAlias }: { title: string; utmAlias?: UtmAlias }) => {
    showModal(<AliasForm utmAlias={utmAlias} type="medium" />, {
      title,
      isFullScreen: false,
      removePadding: true,
      useRadix: true,
    })
  }

  const onAdd = () => openModal({ title: `Add Medium Alias` })
  const onEdit = (utmAlias: UtmAlias) => openModal({ title: `Edit Alias`, utmAlias })

  const onDelete = async (id: string, _workspaceId: string) => {
    await confirm({
      title: `Delete Channel Mapping`,
      description: `Are you sure you want to delete this alias?`,
      variant: `danger`,
    }).then(async () => {
      const result = await store.remove(id)
      if (result.error) {
        logError(`Error deleting medium`, result.error)
        notifyError(`Error deleting medium`)
        return
      } else {
        store.fetchList({ search: `type:medium` })
        notifySuccess(`Alias deleted`)
        return
      }
    })
  }

  const columns = useMemo(() => getColumns({ canEditMapping, onEdit, onDelete }), [media, canEditMapping])

  return {
    media,
    loading,
    canEditMapping,
    onAdd,
    onEdit,
    onDelete,
    columns,
  }
}
